/**
 * Legal document version constants — SINGLE SOURCE OF TRUTH.
 *
 * Imported by BOTH the web app (Vite → source) and the API (compiled → dist) via the
 * `@parse/shared` barrel, so the clickwrap gate, the acceptance-record endpoint, and the
 * served documents can never drift apart.
 *
 * When a material update is published, bump the matching constant HERE (one place). On next
 * load the FE gate sees the new version, re-prompts every user (ToS §19), and each new
 * acceptance row carries the new version. The API rejects (409) any POST whose versions do
 * not equal these, so a stale client cannot record acceptance of a superseded version.
 *
 * Keep these in lockstep with the `**Version X.Y**` header of
 * `apps/web/src/legal/{terms,privacy}.md` (terms "Version 1.2", updated July 30, 2026;
 * privacy "Version 1.1", July 1, 2026).
 */
export const TERMS_VERSION = '1.2';
export const PRIVACY_VERSION = '1.1';

/**
 * Terms versions the acceptance endpoint (POST /api/terms/accept) will RECORD — as opposed to the
 * single current `TERMS_VERSION` the FE prompts for. During a staged rollout the box runs the new
 * `TERMS_VERSION` before the prod FE bundle does, so the prod FE keeps POSTing the PRIOR version for
 * a window; without widening, the box would 409 those clients and lock them out of the app.
 *
 * INVARIANT: `TERMS_VERSION` is always a member (dynamic entry), so the current version is always
 * accepted and a fresh client is never locked out. The extra literal is the prior version still in
 * flight. Acceptance is EXACT-STRING membership — no numeric/whitespace coercion, no "<= current".
 *
 * REMOVE '1.1' once the prod FE bundle ships `TERMS_VERSION='1.2'` (the #239 promote + prod-FE
 * deploy). Added 2026-07-31 for the 1.1→1.2 rollout. No env knob by design: a legal-evidence gate
 * must be reviewable in the diff, not silently reconfigurable via SSM.
 *
 * Privacy has NO equivalent set — it is gated by strict equality on `PRIVACY_VERSION`. A future
 * privacy bump recreates this exact skew and will need the same widening (and the acceptance route's
 * `privacy_version` insert must then switch to the submitted value, as `terms_version` did here).
 */
export const ACCEPTED_TERMS_VERSIONS: ReadonlySet<string> = new Set(['1.1', TERMS_VERSION]);
